import * as React from 'react';
import { toast } from 'react-toastify';
import {
  cancelWaiverRequest,
  invalidateWaiverRequests,
  sendWaiverReminder,
} from '../../services/waivers';
import { WaiverRequestWithCustomerAndEvents } from '../../types/waivers';
import useConfirm from '../useConfirm';
import useUser from '../global/useUser';
import { FooterAction, LoadedUser, ModalData } from '../../interfaces';
import { createModalData } from '../../utils/modal/waiverActivityModal';
import { createFooterAction, openStorageUrl } from '../../helpers/global';
import { AppContext } from '../../context/AppContext/context';

function useWaiverRequestsTableActions({
  setModalData,
  setModalOpen,
}: {
  setModalData?: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
  setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
} = {}) {
  const { user } = useUser<LoadedUser>();
  const { dispatch } = React.useContext(AppContext);

  const confirm = useConfirm();

  // Set waivers page number
  const setWaiversTablePage = React.useCallback(
    (page: number) => {
      dispatch({ type: 'SET_WAIVERS_TABLE_PAGE', payload: page });
    },
    [dispatch],
  );

  const resetWaiversPage = React.useCallback(() => {
    setWaiversTablePage(0);
    invalidateWaiverRequests();
  }, [setWaiversTablePage]);

  const sendReminder = React.useCallback(
    async (waiver: WaiverRequestWithCustomerAndEvents) => {
      try {
        await sendWaiverReminder({
          businessId: user?.business?.id,
          waiverRequestId: waiver.id,
        });
        toast.success('Waiver reminder sent');
      } catch {
        toast.error('Error sending reminder');
      }
    },
    [user?.business?.id],
  );

  const cancelWaiver = React.useCallback(
    async (waiver: WaiverRequestWithCustomerAndEvents) => {
      const accepted = await confirm({
        title: 'Are you sure?',
        message:
          'This action cannot be undone. Once you cancel this waiver request, the customer will no longer be able to submit a waiver through Porter for this appointment',
      });
      if (!accepted) return;

      try {
        await cancelWaiverRequest({
          businessId: user?.business?.id,
          waiverRequestId: waiver.id,
        });

        toast.success('Waiver request canceled');
        resetWaiversPage();
      } catch {
        toast.error('Error canceling waiver request');
      }
    },
    [confirm, resetWaiversPage, user?.business?.id],
  );

  const viewActivity = React.useCallback(
    (waiver: WaiverRequestWithCustomerAndEvents) => {
      if (!setModalData || !setModalOpen) return;
      const tempModalData = createModalData(waiver);
      setModalData(tempModalData);
      setModalOpen(true);
    },
    [setModalData, setModalOpen],
  );

  const viewWaiver = React.useCallback(
    (waiver: WaiverRequestWithCustomerAndEvents) => {
      if (!waiver.signedFileLink) return;
      openStorageUrl(waiver.signedFileLink);
    },
    [],
  );

  const createMoreActions = React.useCallback(
    (waiver: WaiverRequestWithCustomerAndEvents) => {
      const tempActions: FooterAction[] = [];

      if (waiver.status === 'SIGNED') {
        tempActions.push(
          createFooterAction('View waiver', () => viewWaiver(waiver)),
        );
      }

      if (waiver.status === 'SENT') {
        tempActions.push(
          createFooterAction('Send reminder', () => sendReminder(waiver)),
        );
      }

      tempActions.push(
        createFooterAction('View activity', () => viewActivity(waiver)),
      );

      if (waiver.status !== 'CANCELED' && waiver.status !== 'SIGNED') {
        tempActions.push(
          createFooterAction('Cancel waiver', () => cancelWaiver(waiver)),
        );
      }

      return tempActions;
    },
    [viewWaiver, sendReminder, viewActivity, cancelWaiver],
  );

  return {
    createMoreActions,
    resetWaiversPage,
    setWaiversTablePage,
  };
}

export default useWaiverRequestsTableActions;
